import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './ui/dialog';
import { 
  ArrowLeft,
  Crown, 
  Diamond, 
  Award, 
  Star,
  Phone,
  Mail,
  MapPin,
  Calendar,
  TrendingUp,
  DollarSign,
  Heart,
  User,
  Home,
  Utensils,
  Settings,
  MessageSquare,
  Edit,
  Plus,
  Eye,
  FileText,
  Briefcase,
  Users,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle
} from 'lucide-react';

const ClientDetail = ({ clientId, clientData, onBack }) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddNote, setShowAddNote] = useState(false);

  // Données complètes du client (simulées basées sur l'ID)
  const client = clientData || {
    id: 1,
    name: 'Nicolas Beaumont',
    email: 'nicolas.beaumont@email.com',
    phone: '+33 6 12 34 56 78',
    location: 'Paris, France',
    nationality: 'Française',
    vipLevel: 'Gold',
    clientType: 'Individual',
    status: 'Active',
    totalSpent: 458000,
    joinDate: '2019-03-15',
    lastLogin: '2024-12-10',
    satisfaction: 4.9,
    avatar: 'NB',
    
    // Informations personnelles étendues
    personalInfo: {
      dateOfBirth: '1985-04-12',
      profession: 'Directeur Marketing',
      company: 'Beaumont & Associates',
      address: '75 Avenue des Champs-Élysées, 75008 Paris',
      emergencyContact: {
        name: 'Marie Beaumont',
        relation: 'Épouse',
        phone: '+33 6 98 76 54 32'
      },
      documents: {
        passport: 'FR123456789',
        idCard: 'FR987654321',
        expiryDate: '2028-04-12'
      }
    },

    // Préférences détaillées
    preferences: {
      accommodation: ['Suite Master', 'Vue mer', 'Balcon privé', 'Wifi haut débit'],
      dining: ['Cuisine végétarienne', 'Petit-déjeuner continental', 'Room service', 'Wine pairing'],
      activities: ['Yoga matinal', 'Spa wellness', 'Golf', 'Excursions culturelles'],
      services: ['Concierge 24h', 'Transport privé', 'Housekeeping quotidien', 'Laundry'],
      special: ['Silence après 22h', 'Allergique aux fruits de mer', 'Préfère étage élevé']
    },

    // Historique des réservations
    reservations: {
      completed: [
        {
          id: 'RES001',
          property: 'Villa Côte d\'Azur',
          location: 'Cannes, France',
          dates: '2024-07-15 - 2024-07-22',
          duration: 7,
          guests: 4,
          amount: 52500,
          status: 'Completed',
          rating: 5,
          notes: 'Séjour parfait, client très satisfait'
        },
        {
          id: 'RES002',
          property: 'Château Provence',
          location: 'Saint-Tropez, France',
          dates: '2024-04-10 - 2024-04-17',
          duration: 7,
          guests: 6,
          amount: 68000,
          status: 'Completed',
          rating: 4,
          notes: 'Demande spéciale pour chef végétarien accomplie'
        },
        {
          id: 'RES003',
          property: 'Villa Toscane',
          location: 'Florence, Italie',
          dates: '2023-09-20 - 2023-09-27',
          duration: 7,
          guests: 2,
          amount: 35000,
          status: 'Completed',
          rating: 5,
          notes: 'Lune de miel, décorations spéciales appréciées'
        }
      ],
      upcoming: [
        {
          id: 'RES004',
          property: 'Villa Mykonos',
          location: 'Mykonos, Grèce',
          dates: '2025-06-15 - 2025-06-22',
          duration: 7,
          guests: 8,
          amount: 75000,
          status: 'Confirmed',
          specialRequests: ['Chef personnel', 'Yacht excursion', 'Spa à domicile']
        }
      ],
      cancelled: [
        {
          id: 'RES005',
          property: 'Villa Santorini',
          location: 'Santorini, Grèce',
          dates: '2024-08-10 - 2024-08-17',
          duration: 7,
          guests: 4,
          amount: 45000,
          status: 'Cancelled',
          reason: 'Urgence familiale',
          refund: 'Partiel (80%)'
        }
      ]
    },

    // Statistiques détaillées
    stats: {
      totalReservations: 9,
      totalNights: 56,
      averageStayDuration: 6.2,
      preferredDestinations: ['France', 'Italie', 'Grèce'],
      seasonalPattern: {
        spring: 3,
        summer: 4,
        autumn: 2,
        winter: 0
      },
      guestPattern: {
        solo: 1,
        couple: 3,
        family: 4,
        group: 1
      }
    },

    // Communications et notes
    communications: [
      {
        id: 1,
        date: '2024-12-10',
        type: 'Email',
        subject: 'Confirmation réservation Mykonos',
        status: 'Sent',
        author: 'Sarah Martin'
      },
      {
        id: 2,
        date: '2024-12-05',
        type: 'Phone',
        subject: 'Discussion préférences séjour',
        status: 'Completed',
        author: 'Marc Dubois'
      },
      {
        id: 3,
        date: '2024-11-28',
        type: 'WhatsApp',
        subject: 'Demande disponibilités été 2025',
        status: 'Replied',
        author: 'Automated'
      }
    ],

    notes: [
      {
        id: 1,
        date: '2024-12-10',
        author: 'Sarah Martin',
        type: 'Important',
        content: 'Client fidèle depuis 2019. Préfère toujours les destinations méditerranéennes. Très attentif aux détails culinaires (végétarien strict).'
      },
      {
        id: 2,
        date: '2024-07-22',
        author: 'Villa Manager',
        type: 'Feedback',
        content: 'Excellent client, très respectueux des propriétés. A laissé un pourboire généreux au staff. Recommande vivement.'
      },
      {
        id: 3,
        date: '2024-04-17',
        author: 'Chef Cuisine',
        type: 'Preference',
        content: 'Allergique aux fruits de mer. Apprécie particulièrement la cuisine italienne végétarienne. Demande toujours des herbes fraîches.'
      }
    ]
  };

  const getVipIcon = (level) => {
    switch(level) {
      case 'Black': return <Crown className="w-5 h-5" />;
      case 'Diamond': return <Diamond className="w-5 h-5" />;
      case 'Platinum': return <Award className="w-5 h-5" />;
      case 'Gold': return <Star className="w-5 h-5" />;
      default: return <User className="w-5 h-5" />;
    }
  };

  const getVipColor = (level) => {
    switch(level) {
      case 'Black': return 'bg-black text-yellow-500';
      case 'Diamond': return 'bg-blue-500 text-white';
      case 'Platinum': return 'bg-gray-400 text-white';
      case 'Gold': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-200 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'Completed': return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'Confirmed': return <CheckCircle className="w-4 h-4 text-blue-500" />;
      case 'Cancelled': return <XCircle className="w-4 h-4 text-red-500" />;
      default: return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const formatCurrency = (amount) => {
    return `€${amount.toLocaleString()}`;
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={onBack} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center space-x-4">
                <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-xl font-semibold">
                  {client.avatar}
                </div>
                <div>
                  <h1 className="text-2xl font-semibold text-gray-900">{client.name}</h1>
                  <div className="flex items-center space-x-3 mt-1">
                    <div className={`w-8 h-8 rounded-full ${getVipColor(client.vipLevel)} flex items-center justify-center`}>
                      {getVipIcon(client.vipLevel)}
                    </div>
                    <Badge className="bg-green-100 text-green-800">
                      {client.status === 'Active' ? 'Actif' : 'Inactif'}
                    </Badge>
                    <Badge variant="outline">{client.clientType}</Badge>
                    <span className="text-sm text-gray-500">Client depuis {formatDate(client.joinDate)}</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button variant="outline" size="sm">
                <MessageSquare className="w-4 h-4 mr-2" />
                Contacter
              </Button>
              <Button variant="outline" size="sm">
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Stats rapides */}
      <div className="bg-white border-b px-6 py-4">
        <div className="grid grid-cols-5 gap-6">
          <div className="text-center">
            <div className="text-2xl font-semibold text-green-600">{formatCurrency(client.totalSpent)}</div>
            <div className="text-xs text-gray-500 uppercase">Total dépensé</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-blue-600">{client.reservations?.completed?.length || 0}</div>
            <div className="text-xs text-gray-500 uppercase">Séjours terminés</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-purple-600">{client.reservations?.upcoming?.length || 0}</div>
            <div className="text-xs text-gray-500 uppercase">Séjours à venir</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-yellow-600">{client.satisfaction}/5</div>
            <div className="text-xs text-gray-500 uppercase">Satisfaction</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-semibold text-gray-600">{client.stats?.totalNights || 0}</div>
            <div className="text-xs text-gray-500 uppercase">Nuits totales</div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Vue d'ensemble</TabsTrigger>
            <TabsTrigger value="reservations">Réservations</TabsTrigger>
            <TabsTrigger value="preferences">Préférences</TabsTrigger>
            <TabsTrigger value="communications">Communications</TabsTrigger>
            <TabsTrigger value="analytics">Analytics</TabsTrigger>
          </TabsList>

          {/* Vue d'ensemble */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Informations personnelles */}
              <div className="lg:col-span-1">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center space-x-2">
                      <User className="w-5 h-5" />
                      <span>Informations personnelles</span>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <label className="text-sm text-gray-500">Email</label>
                      <div className="flex items-center space-x-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{client.email}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Téléphone</label>
                      <div className="flex items-center space-x-2">
                        <Phone className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{client.phone}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Localisation</label>
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{client.location}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Nationalité</label>
                      <span className="text-sm block">{client.nationality}</span>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Profession</label>
                      <div className="flex items-center space-x-2">
                        <Briefcase className="w-4 h-4 text-gray-400" />
                        <span className="text-sm">{client.personalInfo.profession}</span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-500">Entreprise</label>
                      <span className="text-sm block">{client.personalInfo.company}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="mt-6">
                  <CardHeader>
                    <CardTitle>Préférences rapides</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <h4 className="text-sm font-medium mb-2">Hébergement</h4>
                        <div className="flex flex-wrap gap-1">
                          {(client.preferences?.accommodation || []).slice(0, 3).map((pref, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">{pref}</Badge>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium mb-2">Restauration</h4>
                        <div className="flex flex-wrap gap-1">
                          {(client.preferences?.dining || []).slice(0, 3).map((pref, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">{pref}</Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Activité récente et statistiques */}
              <div className="lg:col-span-2 space-y-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Activité récente</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {(client.communications || []).slice(0, 3).map((comm) => (
                        <div key={comm.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <div>
                              <p className="font-medium text-sm">{comm.subject}</p>
                              <p className="text-xs text-gray-500">{comm.type} • {formatDate(comm.date)} • {comm.author}</p>
                            </div>
                          </div>
                          <Badge variant="outline" className="text-xs">{comm.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Statistiques de séjour</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Durée moyenne</span>
                          <span className="font-medium">{client.stats?.averageStayDuration || 0} jours</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Saison préférée</span>
                          <span className="font-medium">Été</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Type de groupe</span>
                          <span className="font-medium">Famille</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Destinations top</span>
                          <span className="font-medium">France, Italie</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Performance client</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Taux d'annulation</span>
                          <span className="font-medium text-green-600">11%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Recommandation</span>
                          <span className="font-medium text-yellow-600">★★★★★</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Ponctualité paiement</span>
                          <span className="font-medium text-green-600">100%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-sm text-gray-600">Réclamations</span>
                          <span className="font-medium text-green-600">0</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </TabsContent>

          {/* Réservations */}
          <TabsContent value="reservations">
            <div className="space-y-6">
              {/* Réservations à venir */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-5 h-5" />
                      <span>Réservations à venir ({client.reservations?.upcoming?.length || 0})</span>
                    </div>
                    <Button size="sm" className="bg-yellow-600 hover:bg-yellow-700 text-black">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle réservation
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(client.reservations?.upcoming || []).map((reservation) => (
                    <div key={reservation.id} className="p-4 border rounded-lg mb-4 last:mb-0">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          {getStatusIcon(reservation.status)}
                          <div>
                            <h4 className="font-semibold">{reservation.property}</h4>
                            <p className="text-sm text-gray-600">{reservation.location}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold text-lg">{formatCurrency(reservation.amount)}</p>
                          <Badge className="bg-blue-100 text-blue-800">{reservation.status}</Badge>
                        </div>
                      </div>
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Dates:</span>
                          <p className="font-medium">{reservation.dates}</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Durée:</span>
                          <p className="font-medium">{reservation.duration} nuits</p>
                        </div>
                        <div>
                          <span className="text-gray-500">Invités:</span>
                          <p className="font-medium">{reservation.guests} personnes</p>
                        </div>
                        <div>
                          <span className="text-gray-500">ID:</span>
                          <p className="font-medium">{reservation.id}</p>
                        </div>
                      </div>
                      {reservation.specialRequests && (
                        <div className="mt-3 pt-3 border-t">
                          <span className="text-sm text-gray-500">Demandes spéciales:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {reservation.specialRequests.map((request, index) => (
                              <Badge key={index} variant="outline" className="text-xs">{request}</Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Historique des réservations */}
              <Card>
                <CardHeader>
                  <CardTitle>Historique des réservations ({(client.reservations?.completed?.length || 0) + (client.reservations?.cancelled?.length || 0)})</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {[...(client.reservations?.completed || []), ...(client.reservations?.cancelled || [])]
                      .sort((a, b) => new Date(b.dates.split(' - ')[0]) - new Date(a.dates.split(' - ')[0]))
                      .map((reservation) => (
                      <div key={reservation.id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center space-x-3">
                            {getStatusIcon(reservation.status)}
                            <div>
                              <h4 className="font-semibold">{reservation.property}</h4>
                              <p className="text-sm text-gray-600">{reservation.location}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-lg">{formatCurrency(reservation.amount)}</p>
                            <Badge className={
                              reservation.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }>
                              {reservation.status === 'Completed' ? 'Terminé' : 'Annulé'}
                            </Badge>
                          </div>
                        </div>
                        <div className="grid grid-cols-4 gap-4 text-sm mb-3">
                          <div>
                            <span className="text-gray-500">Dates:</span>
                            <p className="font-medium">{reservation.dates}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Durée:</span>
                            <p className="font-medium">{reservation.duration} nuits</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Invités:</span>
                            <p className="font-medium">{reservation.guests} personnes</p>
                          </div>
                          <div>
                            <span className="text-gray-500">
                              {reservation.status === 'Completed' ? 'Note:' : 'Motif:'}
                            </span>
                            <p className="font-medium">
                              {reservation.status === 'Completed' 
                                ? `★${reservation.rating}/5` 
                                : reservation.reason}
                            </p>
                          </div>
                        </div>
                        {reservation.notes && (
                          <div className="pt-3 border-t">
                            <p className="text-sm text-gray-600 italic">{reservation.notes}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Préférences */}
          <TabsContent value="preferences">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(client.preferences || {}).map(([category, items]) => (
                <Card key={category}>
                  <CardHeader>
                    <CardTitle className="capitalize flex items-center justify-between">
                      {category === 'accommodation' && <Home className="w-5 h-5 mr-2" />}
                      {category === 'dining' && <Utensils className="w-5 h-5 mr-2" />}
                      {category === 'activities' && <TrendingUp className="w-5 h-5 mr-2" />}
                      {category === 'services' && <Settings className="w-5 h-5 mr-2" />}
                      {category === 'special' && <AlertTriangle className="w-5 h-5 mr-2" />}
                      <span>
                        {category === 'accommodation' && 'Hébergement'}
                        {category === 'dining' && 'Restauration'}
                        {category === 'activities' && 'Activités'}
                        {category === 'services' && 'Services'}
                        {category === 'special' && 'Demandes spéciales'}
                      </span>
                      <Button variant="ghost" size="sm">
                        <Edit className="w-4 h-4" />
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {(items || []).map((item, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <Heart className="w-4 h-4 text-red-500" />
                          <span className="text-sm">{item}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Communications */}
          <TabsContent value="communications">
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Historique des communications</span>
                    <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Plus className="w-4 h-4 mr-2" />
                      Nouvelle communication
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(client.communications || []).map((comm) => (
                      <div key={comm.id} className="p-4 border rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-3">
                            <MessageSquare className="w-4 h-4 text-blue-500" />
                            <div>
                              <h4 className="font-semibold text-sm">{comm.subject}</h4>
                              <p className="text-xs text-gray-500">{formatDate(comm.date)} • {comm.author}</p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge variant="outline" className="text-xs">{comm.type}</Badge>
                            <Badge className="text-xs bg-green-100 text-green-800">{comm.status}</Badge>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center justify-between">
                    <span>Notes internes</span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => setShowAddNote(true)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Ajouter note
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(client.notes || []).map((note) => (
                      <div key={note.id} className="p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <span className="font-semibold text-sm">{note.author}</span>
                            <Badge variant="outline" className="text-xs">{note.type}</Badge>
                          </div>
                          <span className="text-xs text-gray-500">{formatDate(note.date)}</span>
                        </div>
                        <p className="text-sm text-gray-700">{note.content}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Analytics */}
          <TabsContent value="analytics">
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Valeur Client</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-green-600 mb-2">{formatCurrency(client.totalSpent)}</div>
                    <p className="text-xs text-gray-500">Lifetime Value</p>
                    <div className="mt-4">
                      <div className="text-sm text-gray-600">Moyenne par séjour</div>
                      <div className="text-lg font-semibold">{formatCurrency(Math.round(client.totalSpent / (client.stats?.totalReservations || 1)))}</div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Fidélité</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-blue-600 mb-2">{client.stats?.totalReservations || 0}</div>
                    <p className="text-xs text-gray-500">Réservations totales</p>
                    <div className="mt-4">
                      <div className="text-sm text-gray-600">Depuis</div>
                      <div className="text-lg font-semibold">{new Date().getFullYear() - new Date(client.joinDate).getFullYear()} ans</div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle className="text-sm">Satisfaction</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-2xl font-bold text-yellow-600 mb-2">{client.satisfaction}/5</div>
                    <p className="text-xs text-gray-500">Note moyenne</p>
                    <div className="mt-4">
                      <div className="text-sm text-gray-600">Recommandation</div>
                      <div className="text-lg font-semibold">★★★★★</div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader>
                  <CardTitle>Analyse comportementale</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                      <h4 className="font-medium mb-3">Destinations préférées</h4>
                      {(client.stats?.preferredDestinations || []).map((dest, index) => (
                        <div key={index} className="flex justify-between text-sm mb-1">
                          <span>{dest}</span>
                          <span className="text-gray-500">{Math.round(Math.random() * 5) + 1} séjours</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="font-medium mb-3">Saisonnalité</h4>
                      {Object.entries(client.stats?.seasonalPattern || {}).map(([season, count]) => (
                        <div key={season} className="flex justify-between text-sm mb-1">
                          <span className="capitalize">{season}</span>
                          <span className="text-gray-500">{count} séjours</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="font-medium mb-3">Type de groupe</h4>
                      {Object.entries(client.stats?.guestPattern || {}).map(([type, count]) => (
                        <div key={type} className="flex justify-between text-sm mb-1">
                          <span className="capitalize">{type}</span>
                          <span className="text-gray-500">{count} séjours</span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h4 className="font-medium mb-3">Tendances</h4>
                      <div className="text-sm space-y-1">
                        <p>• Durée moyenne: {client.stats?.averageStayDuration || 0} jours</p>
                        <p>• Réservation anticipée: 45 jours</p>
                        <p>• Taux retour: 89%</p>
                        <p>• Spending trend: ↗ +15%</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Modal d'ajout de note */}
      <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter une note interne</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium">Type de note</label>
              <select className="w-full mt-1 p-2 border rounded-md">
                <option>Information</option>
                <option>Préférence</option>
                <option>Feedback</option>
                <option>Important</option>
                <option>Alerte</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Contenu</label>
              <textarea 
                className="w-full mt-1 p-2 border rounded-md h-24 resize-none"
                placeholder="Saisissez votre note..."
              />
            </div>
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setShowAddNote(false)}>
                Annuler
              </Button>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                Ajouter la note
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientDetail;